import { PageHeader } from "@/components/common/PageHeader";
import { Link } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { Radio, ExternalLink, Info, Video, LifeBuoy, CalendarClock } from "lucide-react";

export default function LiveVideo() {
  const { settings } = useSite();
  const embed = settings.livestream_embed_url;
  const joinUrl = settings.live_join_url;

  const STEPS = [
    "Click the Join Live Session button below at session time.",
    "Allow camera and microphone access if prompted.",
    "Enter your name so your coach can see you.",
    "Have water and space ready — let's train!",
  ];

  return (
    <div data-testid="live-page">
      <PageHeader eyebrow="Stream" titleLeft="LIVE " titleRight="SESSION" description="Join live coaching sessions and classes from anywhere." image="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1600&q=70&auto=format&fit=crop" />
      <section className="bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6">
          <div className="flex items-center gap-2 text-brand"><Radio className="h-5 w-5 animate-pulse" /><span className="font-condensed text-sm font-semibold uppercase tracking-wide">Live Coaching</span></div>

          {/* Join CTA */}
          <div className="mt-4 rounded-xl border border-border bg-card p-8 text-center">
            <Video className="mx-auto h-12 w-12 text-brand" />
            <h2 className="mt-4 display-title text-4xl">Join The Live Session</h2>
            <p className="mx-auto mt-2 max-w-md text-muted-foreground">Hosted on Zoom, Google Meet, or YouTube Live. Tap to join when it's time.</p>
            {joinUrl ? (
              <a href={joinUrl} target="_blank" rel="noopener noreferrer" data-testid="live-join-button" className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-brand px-8 py-4 text-base font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors active:scale-[0.98]">Join Live Session <ExternalLink className="h-5 w-5" /></a>
            ) : (
              <p className="mt-6 text-sm text-muted-foreground" data-testid="live-no-session">No live session link is set right now. Please check back at session time.</p>
            )}
          </div>

          {settings.livestream_schedule && (
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-5" data-testid="live-schedule">
              <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
              <div><p className="font-condensed text-sm font-semibold uppercase tracking-wide">Schedule & Reminders</p><p className="mt-1 text-sm text-muted-foreground">{settings.livestream_schedule}</p></div>
            </div>
          )}

          {/* Instructions */}
          <div className="mt-6 rounded-xl border border-border bg-card p-6">
            <h3 className="flex items-center gap-2 display-title text-2xl"><Info className="h-5 w-5 text-brand" /> How To Join</h3>
            <ol className="mt-4 space-y-2.5">
              {STEPS.map((s, i) => (
                <li key={s} className="flex gap-3 text-sm text-muted-foreground"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-bold text-white">{i + 1}</span>{s}</li>
              ))}
            </ol>
          </div>

          {/* Optional embed */}
          {embed && (
            <div className="mt-6 overflow-hidden rounded-xl border border-border bg-black">
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <iframe data-testid="livestream-embed" src={embed} title="Trilogy Live" className="absolute inset-0 h-full w-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </div>
          )}

          {/* Backup / support */}
          <div className="mt-6 rounded-xl border border-border bg-muted/30 p-6">
            <h3 className="flex items-center gap-2 display-title text-2xl"><LifeBuoy className="h-5 w-5 text-brand" /> Having Trouble?</h3>
            <p className="mt-2 text-sm text-muted-foreground">If you can't connect, use the backup link or contact support and we'll help you get in.</p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              {settings.livestream_backup_url && (
                <a href={settings.livestream_backup_url} target="_blank" rel="noopener noreferrer" data-testid="livestream-backup-link" className="inline-flex items-center justify-center gap-2 rounded-md border border-border px-5 py-2.5 text-sm font-semibold uppercase tracking-wide hover:bg-accent transition-colors">Open Backup Stream <ExternalLink className="h-4 w-4" /></a>
              )}
              <Link to="/support" data-testid="live-support-link" className="inline-flex items-center justify-center gap-2 rounded-md bg-brand px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors"><LifeBuoy className="h-4 w-4" /> Contact Support</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
